import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiX, FiPhone, FiArrowRight, FiLoader } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { sendOtp, loginWithOtp, loginWithPhone } from "../api/services/auth";
import { useToki } from "../context/TokiContext";
import { useRegion } from "../context/RegionContext";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import useEscapeKey from "../hooks/useEscapeKey";
import useDialogFocus from "../hooks/useDialogFocus";
import useLoginTracking from "../hooks/useLoginTracking";

/**
 * @param {string} [trigger] - what asked for the login (send_cv, like, interview, …); only
 *   used for analytics. assessmentId/companyId likewise, when a job is in view.
 */
const LoginModal = ({ isOpen, onClose, onSuccess, trigger, assessmentId, companyId }) => {
    const { t } = useTranslation();
    const { setPhoneUser } = useToki();
    const loginTracking = useLoginTracking({ isOpen, trigger, assessmentId, companyId });
    const [step, setStep] = useState("input"); // input | otp
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [countdown, setCountdown] = useState(0);
    const otpRefs = useRef([]);

    useBodyScrollLock(isOpen);
    useEscapeKey(isOpen, onClose);
    const panelRef = useDialogFocus(isOpen);

    // Get country config from centralized region config
    const region = useRegion();
    const countryConfig = {
        code: region.countryCode,
        flag: region.flag,
        phoneLength: region.phoneLength,
        placeholder: region.phonePlaceholder,
        format: region.phoneFormat,
    };

    useEffect(() => {
        if (!isOpen) {
            setStep("input");
            setPhone("");
            setOtp(["", "", "", "", "", ""]);
            setError("");
            setCountdown(0);
        }
    }, [isOpen]);

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    // Broadcast open/close so the layout can hide the bottom nav bar while this
    // modal is up (it would otherwise overlap the submit button). Modals opened
    // from any page use their own local state, so a global signal is needed.
    useEffect(() => {
        if (!isOpen) return;
        window.dispatchEvent(new CustomEvent("diil:loginModal", { detail: { open: true } }));
        return () => window.dispatchEvent(new CustomEvent("diil:loginModal", { detail: { open: false } }));
    }, [isOpen]);

    const formatPhoneDisplay = (value) => {
        const digits = value.replace(/\D/g, "");
        return countryConfig.format(digits);
    };

    const handlePhoneChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, countryConfig.phoneLength);
        setPhone(digits);
        setError("");
    };

    /**
     * Enter presses the button below. A phone number typed on a keyboard ends with Enter —
     * it is a one-field form and every other one on the web submits that way — and without
     * this the key did nothing, leaving the number typed and the code unsent.
     *
     * Guarded by the same condition that disables the button, so Enter cannot send a half
     * number or fire a second request while the first is in flight.
     */
    const handlePhoneKeyDown = (e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        if (isLoading || phone.length !== countryConfig.phoneLength) return;
        handlePhoneSubmit();
    };

    const handlePhoneSubmit = async () => {
        if (phone.length !== countryConfig.phoneLength) {
            setError(t("auth.invalidPhone"));
            loginTracking.failed("invalid_phone", `length ${phone.length}/${countryConfig.phoneLength}`);
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            const formattedPhone = `${countryConfig.code}${phone}`;
            if (region.authMethod !== 'otp') {
                const result = await loginWithPhone(formattedPhone, region.code);
                if (result.success && result.data?.token) {
                    if (setPhoneUser) {
                        setPhoneUser({
                            id: result.data.candidateId,
                            firstName: result.data.firstName,
                            lastName: result.data.lastName,
                            phone: result.data.phone || formattedPhone,
                            profileUrl: result.data.profileUrl,
                            isNewUser: result.data.isNewUser
                        });
                    }
                    loginTracking.succeeded({ isNewUser: result.data.isNewUser, method: "phone" });
                    onSuccess?.();
                } else {
                    setError(result.error || t("auth.loginError"));
                    loginTracking.failed("login_failed", result.error);
                }
            } else {
                const result = await sendOtp(formattedPhone);
                if (result.success) {
                    loginTracking.otpSent();
                    setStep("otp");
                    setCountdown(60);
                    setTimeout(() => otpRefs.current[0]?.focus(), 100);
                } else {
                    setError(result.error || t("auth.sendOtpError"));
                    loginTracking.failed("otp_send_failed", result.error);
                }
            }
        } catch (err) {
            setError(region.authMethod !== 'otp' ? t("auth.loginError") : t("auth.sendOtpError"));
            loginTracking.failed(region.authMethod !== 'otp' ? "login_error" : "otp_send_error", err?.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpChange = (index, value) => {
        const digit = value.replace(/\D/g, "").slice(-1);
        const newOtp = [...otp];
        newOtp[index] = digit;
        setOtp(newOtp);
        setError("");

        if (digit && index < 5) otpRefs.current[index + 1]?.focus();
        if (digit && index === 5 && newOtp.every((d) => d)) handleVerifyOtp(newOtp.join(""));
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        if (pastedData.length === 6) {
            const newOtp = pastedData.split("");
            setOtp(newOtp);
            handleVerifyOtp(pastedData);
        }
    };

    const handleVerifyOtp = async (otpCode) => {
        setIsLoading(true);
        setError("");

        try {
            const formattedPhone = `${countryConfig.code}${phone}`;
            const result = await loginWithOtp(formattedPhone, otpCode);

            if (result.success && result.data?.token) {
                if (setPhoneUser) {
                    setPhoneUser({
                        id: result.data.candidateId,
                        firstName: result.data.firstName,
                        lastName: result.data.lastName,
                        phone: result.data.phone || '',
                        profileUrl: result.data.profileUrl,
                        isNewUser: result.data.isNewUser
                    });
                }
                loginTracking.succeeded({ isNewUser: result.data.isNewUser, method: "otp" });
                onSuccess?.();
            } else {
                setError(result.error || t("auth.invalidOtp"));
                setOtp(["", "", "", "", "", ""]);
                otpRefs.current[0]?.focus();
                loginTracking.failed("otp_invalid", result.error);
            }
        } catch (err) {
            setError(t("auth.verifyError"));
            setOtp(["", "", "", "", "", ""]);
            loginTracking.failed("otp_verify_error", err?.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = () => {
        if (countdown === 0) {
            setOtp(["", "", "", "", "", ""]);
            handlePhoneSubmit();
        }
    };

    if (!isOpen) return null;

    // Portaled to document.body, same as PostJobModal and NotificationPanel. Header
    // renders this modal from inside Layout's scroll-linked header wrapper, which turns
    // into `fixed z-30` on the first scrolled pixel — a stacking context that capped this
    // overlay below the sidebar (z-40) and the mobile bottom nav (z-50).
    return createPortal(
        <div className="fixed inset-0 z-[120] flex items-end md:items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

            {/* Modal */}
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="login-modal-title"
                tabIndex={-1}
                className="relative bg-white w-full md:w-[500px] md:rounded-3xl rounded-t-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col focus:outline-none"
            >
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-100">
                    <h2 id="login-modal-title" className="text-xl font-bold text-[#020618]">{t("auth.login")}</h2>
                    <button onClick={onClose} aria-label={t("common.close", "Хаах")} className="w-10 h-10 rounded-full bg-[#F4F7FB] flex items-center justify-center hover:bg-gray-200 transition-colors text-[#1e293b]">
                        <FiX className="text-xl" />
                    </button>
                </div>

                <div className="p-6 flex-1 overflow-y-auto" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)" }}>
                    {step === "input" ? (
                        <>
                            <div className="mt-4">
                                <p className="hidden md:block text-[#64748B] text-[15px] mb-8">
                                    {t("auth.phoneDescription")}
                                </p>

                                <div className="mb-8">
                                    <label className="block text-[15px] font-medium text-[#020618] mb-3">
                                        {t("auth.phoneNumber")}
                                    </label>
                                    <div className="flex items-center border-2 border-[#E2E8F0] rounded-xl overflow-hidden focus-within:border-[#020618] transition-colors h-14">
                                        <div className="flex items-center gap-2 px-5 py-3 bg-[#F8FAFC] border-r border-[#E2E8F0] h-full">
                                            <span className="text-lg">{countryConfig.flag}</span>
                                            <span className="text-[#020618] font-medium">+{countryConfig.code}</span>
                                        </div>
                                        <input
                                            type="tel"
                                            value={formatPhoneDisplay(phone)}
                                            onChange={handlePhoneChange}
                                            onKeyDown={handlePhoneKeyDown}
                                            placeholder={countryConfig.placeholder}
                                            className="flex-1 px-5 py-3 outline-none text-[#020618] text-lg font-medium placeholder-[#94A3B8] tracking-widest h-full"
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                {error && <p className="text-[#F43F5E] text-sm mb-4">{error}</p>}

                                <button
                                    onClick={handlePhoneSubmit}
                                    disabled={isLoading || phone.length !== countryConfig.phoneLength}
                                    className="auth-submit-btn w-full py-3 bg-[#020618] text-white rounded-xl font-medium text-[15px] flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#1D293D] transition-colors shadow-sm"
                                >
                                    {isLoading ? <FiLoader className="animate-spin text-xl" /> : (
                                        <>
                                            {region.authMethod !== 'otp' ? t("auth.login") : t("auth.sendOtp")} <FiArrowRight className="text-lg" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col">
                            <h3 className="text-xl font-bold text-[#020618] mb-2 text-center">{t("auth.enterOtp")}</h3>

                            <p className="text-center text-[#64748B] text-sm mb-5 max-w-xs mx-auto">
                                {t("auth.otpSentTo")}
                                <span className="block font-bold text-[#020618] mt-1">+{countryConfig.code} {formatPhoneDisplay(phone)}</span>
                            </p>

                            <div className="flex gap-2 justify-center mb-5">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (otpRefs.current[index] = el)}
                                        type="text"
                                        inputMode="numeric"
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        onPaste={handleOtpPaste}
                                        className={`w-11 h-12 text-center text-xl font-bold border-2 rounded-lg outline-none transition-colors ${digit ? 'border-[#020618] text-[#020618]' : 'border-[#E2E8F0] focus:border-[#020618]'}`}
                                        maxLength={1}
                                    />
                                ))}
                            </div>

                            {error && <p className="text-[#F43F5E] text-sm text-center mb-4">{error}</p>}
                            {isLoading && <div className="flex justify-center mb-4"><FiLoader className="animate-spin text-2xl text-[#020618]" /></div>}

                            <div className="text-center mb-4">
                                <button
                                    onClick={handleResend}
                                    disabled={countdown > 0}
                                    className="text-sm font-medium disabled:opacity-50 text-[#64748B]"
                                >
                                    {countdown > 0 ? (
                                        <span>{t("auth.resendIn")} {countdown}s</span>
                                    ) : (
                                        <span className="text-[#020618] underline">{t("auth.resendOtp")}</span>
                                    )}
                                </button>
                            </div>

                            <button
                                onClick={() => { setStep("input"); setOtp(["", "", "", "", "", ""]); }}
                                className="w-full py-3 border-2 border-[#E2E8F0] text-[#020618] rounded-xl font-medium hover:bg-[#F8FAFC] transition-colors"
                            >
                                {t("auth.changePhone")}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default LoginModal;
